"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { AlphaSummaryPanel } from "@/components/tools/stats-clinical/krippendorff-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type AlphaMetric,
  alphaByMetric,
  type BootstrapResult,
  bootstrapAlpha,
  metricAvailable,
  prepareReliabilityData,
} from "@/lib/tool-kit/stats-clinical/krippendorff";
import {
  type HeaderMode,
  parseLabelTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const KRIPPENDORFF_ALPHA_CALCULATOR_STRINGS = {
  dataLabel:
    "Ratings — one unit (item) per row, one coder per column; leave a missing rating blank or write NA",
  dataHint:
    "Paste from a spreadsheet, or separate values with tabs, commas or spaces. Words are fine for nominal data; ordinal, interval and ratio need numbers. A header row is optional.",
  dataPlaceholder: "1\t1\tNA\t1\n2\t2\t3\t2\n3\t3\t3\t3",
  dataExample:
    "Coder A\tCoder B\tCoder C\tCoder D\n1\t1\tNA\t1\n2\t2\t3\t2\n3\t3\t3\t3\n3\t3\t3\t3\n2\t2\t2\t2\n1\t2\t3\t4\n4\t4\t4\t4\n1\t1\t2\t1\n2\t2\t2\t2\nNA\t5\t5\t5\nNA\tNA\t1\t1\nNA\tNA\t3\tNA",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  layoutLabel: "Layout",
  layoutUnits: "Each row is a unit, each column a coder",
  layoutCoders: "Each row is a coder, each column a unit",
  metricLabel: "Level of measurement",
  metricHint:
    "Nominal: categories with no order. Ordinal: ordered categories. Interval: equal steps, no true zero. Ratio: equal steps with a true zero.",
  metricNominal: "Nominal",
  metricOrdinal: "Ordinal",
  metricInterval: "Interval",
  metricRatio: "Ratio",
  metricFallback:
    "{metric} needs numeric values (and, for ratio, none below zero), so the nominal result is shown.",
  unreadable: "No ratings could be read. Check the layout setting.",
  unitsRead: "one: {count} unit|other: {count} units",
  codersRead: "one: {count} coder|other: {count} coders",
  valuesRead: "one: {count} rating|other: {count} ratings",
  droppedUnits:
    "one: {count} unit with fewer than two ratings was set aside, because it cannot be compared.|other: {count} units with fewer than two ratings were set aside, because they cannot be compared.",
  needMore:
    "Krippendorff's alpha needs at least two units with two or more ratings each.",
  enterPrompt:
    "Paste your coders' ratings to see Krippendorff's alpha for each level of measurement.",
  results: "Results",
  alphaFor: "Krippendorff's α, {metric}",
  unitsUsed: "Units compared",
  pairableValues: "Pairable values (n)",
  interpretation: "α = {alpha} is {band} on Krippendorff's benchmarks.",
  interpretationNote:
    "Krippendorff (2004) suggests relying on α ≥ 0.800, drawing only tentative conclusions from 0.667 ≤ α < 0.800, and discarding data below 0.667 — a rule of thumb whose cut-offs depend on the cost of errors in your field.",
  bandReliable: "reliable (0.800 or above)",
  bandTentative: "tentative (0.667 to 0.800)",
  bandUnreliable: "unreliable (below 0.667)",
  undefinedAlpha:
    "α is undefined here: the coders used only one value, so there is no variation to agree about.",
  allMetrics: "All levels of measurement",
  columnMetric: "Metric",
  columnAlpha: "α",
  columnObserved: "Observed disagreement (Dₒ)",
  columnExpected: "Expected disagreement (Dₑ)",
  bootstrapHeading: "Bootstrap interval",
  bootstrapNote:
    "Resamples the units with replacement 2,000 times and recomputes α each time. It uses a fixed random seed, so the same data always give the same interval.",
  bootstrapRun: "Compute 95% bootstrap interval",
  bootstrapFailed: "Too few units to bootstrap.",
  bootstrapResult:
    "95% bootstrap interval: {lower} to {upper} ({count} usable resamples).",
  bootstrapBelow:
    "Probability that α falls below 0.667: {below667}. Below 0.800: {below800}.",
  matrixHeading: "Coincidence matrix",
  matrixNote:
    "Each unit's ratings are paired in every order and each pair counts 1/(m − 1), where m is the number of ratings that unit received. Row totals are the n_c that drive the expected disagreement.",
  matrixTooBig: "The coincidence matrix is shown for up to 12 distinct values.",
  total: "Total",
  howCalculated: "How this was calculated",
  workingAlpha: "α = 1 − Dₒ / Dₑ = 1 − {observed} / {expected} = {alpha}",
  workingDetail:
    "n = {n} pairable values in {units} units. Dₒ is the average squared difference between values paired within a unit; Dₑ is the same average over every pair of values in the data. The squared difference is 0 or 1 for nominal data, (c − k)² for interval, ((c − k)/(c + k))² for ratio, and a cumulative-frequency distance for ordinal.",
  report:
    "Krippendorff's α ({metric}) = {alpha}{interval}, {units} units, {values} pairable values.",
  reportInterval: ", 95% bootstrap CI [{lower}, {upper}]",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

type Layout = "units" | "coders";

const METRIC_OPTIONS: readonly AlphaMetric[] = [
  "nominal",
  "ordinal",
  "interval",
  "ratio",
];

const METRIC_LABEL_KEY = {
  nominal: "metricNominal",
  ordinal: "metricOrdinal",
  interval: "metricInterval",
  ratio: "metricRatio",
} as const;

function transpose(rows: string[][]): string[][] {
  const width = Math.max(0, ...rows.map((row) => row.length));
  return Array.from({ length: width }, (_, j) =>
    rows.map((row) => row[j] ?? "")
  );
}

export default function KrippendorffAlphaCalculatorWidget() {
  const strings = useToolUi(KRIPPENDORFF_ALPHA_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [layout, setLayout] = useState<Layout>("units");
  const [metric, setMetric] = useState<AlphaMetric>("nominal");
  const [bootstrap, setBootstrap] = useState<{
    key: string;
    result: BootstrapResult | null;
  } | null>(null);

  const data = useMemo(() => {
    const table = parseLabelTable(text, header);
    const rows = layout === "units" ? table.rows : transpose(table.rows);
    return {
      prepared: prepareReliabilityData(
        rows.map((row) => row.map((cell) => cell.trim()))
      ),
      coders: layout === "units" ? table.columns : table.rows.length,
    };
  }, [text, header, layout]);
  const { prepared, coders } = data;
  const summaries = useMemo(
    () => (prepared.units.length >= 2 ? alphaByMetric(prepared) : []),
    [prepared]
  );
  const effective: AlphaMetric = metricAvailable(metric, prepared)
    ? metric
    : "nominal";
  const key = `${text}|${header}|${layout}|${effective}`;
  const entered = text.trim() !== "";
  const plural = (
    name: "unitsRead" | "codersRead" | "valuesRead" | "droppedUnits",
    n: number
  ) => tPlural(strings[name], locale, n, { count: fmt(n) }).trim();
  const valueCount = prepared.units.reduce((sum, unit) => sum + unit.length, 0);

  return (
    <div className="space-y-6">
      <PasteBox
        actions={[
          {
            label: strings.loadExample,
            onClick: () => {
              setHeader("auto");
              setLayout("units");
              setText(strings.dataExample);
            },
          },
          { label: strings.clear, onClick: () => setText("") },
        ]}
        header={{
          label: strings.headerLabel,
          value: header,
          onChange: setHeader,
          options: [
            { value: "auto", label: strings.headerAuto },
            { value: "yes", label: strings.headerYes },
            { value: "no", label: strings.headerNo },
          ],
        }}
        hint={strings.dataHint}
        id="ka-data"
        label={strings.dataLabel}
        onChange={setText}
        placeholder={strings.dataPlaceholder}
        rows={8}
        value={text}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="ka-layout"
          label={strings.layoutLabel}
          onChange={setLayout}
          options={[
            { value: "units", label: strings.layoutUnits },
            { value: "coders", label: strings.layoutCoders },
          ]}
          value={layout}
        />
        <div className="sm:col-span-2">
          <PillGroup
            label={strings.metricLabel}
            name="ka-metric"
            onChange={setMetric}
            options={METRIC_OPTIONS.map((value) => ({
              value,
              label: strings[METRIC_LABEL_KEY[value]],
            }))}
            value={metric}
          />
          <p className={`${HINT} mt-2`}>{strings.metricHint}</p>
        </div>
      </div>
      {entered && summaries.length > 0 ? (
        <p className={HINT}>
          {[
            plural("unitsRead", prepared.units.length),
            plural("codersRead", coders),
            plural("valuesRead", valueCount),
            prepared.droppedUnits > 0
              ? plural("droppedUnits", prepared.droppedUnits)
              : "",
          ]
            .filter((part) => part !== "")
            .join(" · ")}
        </p>
      ) : null}
      {effective !== metric && summaries.length > 0 ? (
        <Notice>
          {t(strings.metricFallback, {
            metric: strings[METRIC_LABEL_KEY[metric]],
          })}
        </Notice>
      ) : null}
      {summaries.length > 0 ? (
        <AlphaSummaryPanel
          bootstrap={bootstrap?.key === key ? bootstrap.result : undefined}
          data={prepared}
          fmt={fmt}
          metric={effective}
          onBootstrap={() =>
            setBootstrap({ key, result: bootstrapAlpha(prepared, effective) })
          }
          strings={strings}
          summaries={summaries}
        />
      ) : null}
      {entered && summaries.length === 0 ? (
        <ErrorNotice>
          {prepared.totalUnits === 0 ? strings.unreadable : strings.needMore}
        </ErrorNotice>
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
